import classNames from 'classnames';
import React, { forwardRef, Ref, useId, useState } from 'react';
import Checkbox from '../Checkbox';
import InlineError from '../InlineError';
import Tooltip from '../Tooltip';
import './CheckboxGroup.scss';

interface CheckboxOption {
  label: string;
  value: string | number;
}

interface CheckboxGroupProps {
  label: React.ReactNode | React.ReactNode[];
  hideLabel?: boolean;
  required?: boolean;
  options: CheckboxOption[] | string[];
  name: string;
  value?: string[];
  onChange?: (chosenOptions: (string | number)[]) => void;
  onBlur?: () => void;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  error?: string;
  pii?: boolean;
}

const CheckboxGroup = forwardRef<HTMLInputElement, CheckboxGroupProps>(
  (
    {
      label,
      hideLabel = false,
      required = false,
      pii = false,
      options,
      name,
      value = [],
      onChange,
      onBlur,
      tooltip,
      tooltipAriaLabel,
      error
    },
    ref
  ) => {
    const id = useId();
    const [selectedOptions, setSelectedOptions] = useState<(string | number)[]>(value);

    const checkboxes = options.map((option, index) => (
      <Checkbox
        key={`${name}-${index}`}
        label={typeof option === 'string' ? option : option.label}
        name={name}
        onChange={(_, isChecked) => {
          let newSelectedOptions = [...selectedOptions];

          if (isChecked) {
            newSelectedOptions.push(typeof option === 'string' ? option : option.value);
          } else {
            newSelectedOptions = newSelectedOptions.filter((value) =>
              typeof option === 'string' ? value !== option : value !== option.value
            );
          }
          setSelectedOptions(newSelectedOptions);

          // send data to parent component
          onChange(newSelectedOptions);
        }}
        onBlur={onBlur}
        checked={selectedOptions.includes(typeof option === 'string' ? option : option.value)}
        error={!!error}
        inputProps={{
          'aria-describedby': error ? `error-${id}` : undefined,
          'aria-required': required
        }}
        ref={index === 0 ? ref : null}
      />
    ));

    return (
      <fieldset
        className={classNames('mss-form-control mss-checkbox-group', {
          'ctHidden pii-data': pii
        })}
        aria-describedby={error ? `error-${id}` : undefined}
        aria-labelledby={`checkbox-group-label-${id}`}
        aria-invalid={!!error}
      >
        {/*
          Using a `<div />` instead of a `<legend />` due to an issue in JAWS that prevents 
          interactive elements from working when placed inside a `<legend />`.
          See related GitHub issue: https://github.com/FreedomScientific/standards-support/issues/402
        */}
        <div
          id={`checkbox-group-label-${id}`}
          className={classNames('mss-form-control-label', {
            'sr-only': hideLabel
          })}
        >
          {label}
          {required && (
            <span className="mss-form-control-required" aria-hidden>
              *
            </span>
          )}
          {tooltip && (
            <Tooltip content={tooltip} buttonProps={{ 'aria-label': tooltipAriaLabel }} />
          )}
        </div>
        {checkboxes}
        <div role="alert">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {((error as any) instanceof String || typeof error === 'string') && (
            <InlineError error={error as string} id={id} />
          )}
        </div>
      </fieldset>
    );
  }
);

export default CheckboxGroup as (
  props: CheckboxGroupProps & { ref?: Ref<HTMLInputElement> }
) => JSX.Element;
